import React, { useEffect, useState , useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Modal,
  Chip,
  Stack,
  Card,
  CardMedia,
  CardContent,
  Button
} from '@mui/material';
import DOMPurify from 'dompurify';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Carousel from '../../components/blog/blogCarousel';
import { apigetPublic, apidelete } from '../../service/api';
import LandingPageNav from '../../components/landingpage/LandingPageNav';
import EditBlogPost from './Edit';
import Footer from '../../components/Footer/footer';

const isObjectId = (s) => /^[a-f\d]{24}$/i.test(s || '');

const ViewBlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const handleOpenEdit = () => setOpenEditDialog(true);
  const handleCloseEdit = () => setOpenEditDialog(false);

  console.log('Logged in user:', user);

  const handleDelete = async () => {
    const confirm = window.confirm('Are you sure you want to delete this blog post?');
    if (!confirm) return;

    try {
      // Use the post's real ObjectId (the URL param may be a slug).
      const res = await apidelete(`blog/${post._id}`);
      if (res.status === 200) {
        alert(res.data.message || 'Post deleted successfully');
        window.location.href = '/blog'; // Redirect after delete
      } else {
        alert('Failed to delete post');
      }
    } catch (err) {
      console.error('Delete failed:', err);
      alert('An error occurred while deleting the post.');
    }
  };

  const fetchBlogPost = async () => {
    try {
      const result = await apigetPublic(`blog/${id}`);
      if (result?.status === 200) {
        setPost(result.data);
        // Canonicalize a legacy ObjectId URL to the SEO slug URL, preserving the
        // dashboard vs. public path prefix.
        if (isObjectId(id) && result.data?.slug) {
          const prefix = pathname.startsWith('/dashboard/blog') ? '/dashboard/blog' : '/blog';
          navigate(`${prefix}/${result.data.slug}`, { replace: true });
        }
      }
    } catch (error) {
      console.error('Failed to fetch blog post:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogPost();
  }, [id]);

    const safeHtml = useMemo(
  () => DOMPurify.sanitize(post?.body ?? ''),
  [post?.body]
);

  if (loading) {
    return (
      <Modal open>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 300,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading blog post...</Typography>
        </Box>
      </Modal>
    );
  }

  if (!post) {
    return (
      <Container sx={{ pt: 12 }}>
        <Typography variant="h5" align="center">Blog post not found.</Typography>
      </Container>
    );
  }

  const canEdit = user?.role === 'admin' || (user?.role === 'marketing');
  const handleRedirect = () => {
    window.location.href = post.callToActionLink;
  };




  return (
    <>
      <LandingPageNav />
      <Box
        sx={{
          backgroundImage: 'url(https://res.cloudinary.com/ddhrdxc7d/image/upload/v1763996573/Background_Image_-_Blog_Pages_and_individual_LO_pages_2_oltdqe.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          minHeight: '100vh',
          pt: 8,
          
       
        }}
      >
        <Box sx={{ backgroundColor: 'rgba(255,255,255,0.9)', minHeight: '100vh', px: 2 }}>
          <Container maxWidth="md">
            <Card elevation={3} sx={{ mb: 4 }}>
              {post.imageUrl && (
                <CardMedia
                  component="img"
                  height="300"
                  image={post.imageUrl}
                  alt={post.title}
                />
              )}
              <CardContent>
                <Typography variant="h4" gutterBottom>
                  {post.title}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  {new Date(post.createdAt).toLocaleDateString()}
                </Typography>

                {post.categories && (
                  <Stack direction="row" spacing={1} mt={2} flexWrap="wrap">
                    {post.categories.map((cat, i) => (
                      <Chip key={i} label={cat} variant="outlined" />
                    ))}
                  </Stack>
                )}

             <Box
  sx={{
    mt: 4,
    '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1 },
    '& p': { mb: 1.5 },
    '& h1,& h2,& h3': { mt: 2.5, mb: 1 },
    '& blockquote': { pl: 2, borderLeft: '4px solid #eee', color: 'text.secondary' },
    '& ul, & ol': { pl: 3 },
  }}
  dangerouslySetInnerHTML={{ __html: safeHtml }}
/>

                {/* Call to Action Section with Blue/Grey Background and Yellow Button */}
                {post.callToActionTitle && (
                  <Box sx={{
                    mt: 4,
                    backgroundColor: '#AD965F', // Blue/grey background for CTA
                    padding: '20px',
                    borderRadius: '8px',
                    textAlign: 'center',
                    boxShadow: 3,
                  }}>
                    <Typography variant="h4" color="white">
                      {post.callToActionTitle}
                    </Typography>
                    <Button
                      variant="contained"
                      sx={{
                        mt: 2,
                        backgroundColor: '#3A3E49', // grey button color
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#C1C7C9', // Hover effect for yellow button
                        }
                      }}
                      onClick={handleRedirect}
                    >
                      {post.callToActionButtonText}
                    </Button>
                  </Box>
                )}

                {canEdit && (
                  <>
                    <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
                      <Button variant="outlined" color="primary" onClick={handleOpenEdit}>
                        Edit
                      </Button>
                      <Button variant="outlined" color="error" onClick={handleDelete}>
                        Delete
                      </Button>
                    </Stack>

                    <EditBlogPost
                      open={openEditDialog}
                      handleClose={handleCloseEdit}
                      id={post._id}
                      fetchPosts={fetchBlogPost}
                    />
                  </>
                )}

              </CardContent>
            </Card>
            <Carousel />
          </Container>
        </Box>
      </Box>
      
                <Footer />
    </>
  );
};

export default ViewBlogPost;
